/**
 * @description Gateway-aware card entry wrapper.
 * Automatically renders HostedPaymentFields (Elavon HPF) or PaymentCardForm (legacy)
 * based on the active gateway's capabilities. Drop-in replacement for PaymentCardForm.
 *
 * Key features:
 * - Detects gateway type via getClientConfig() on mount
 * - Renders HPF form layout immediately (no spinner) while detecting
 * - Passes pre-fetched clientConfig to HPF hook to avoid redundant API call
 * - Falls back to legacy form only if gateway detection explicitly fails
 * - Same props interface as PaymentCardForm (plus optional amount/currency/description
 *   which are passed through to HostedPaymentFields for HPF session creation)
 *
 * @dependencies
 * - paymentSessionService.getClientConfig(): Returns { gatewayType, features }
 * - HostedPaymentFields: Elavon HPF card entry component
 * - PaymentCardForm: Legacy/mock card entry component
 *
 * @notes
 * - No loading spinner — HPF form layout renders immediately with empty styled
 *   field wrappers. iFrames silently populate when the SDK loads.
 * - Gateway detection result is cached for the component's lifetime (single API call).
 * - If gateway detection fails (network error, no gateway configured), falls back to
 *   legacy PaymentCardForm so the UI is never broken.
 */

import React, { useState, useEffect, useRef } from 'react';
import PaymentCardForm from './PaymentCardForm';
import HostedPaymentFields from './HostedPaymentFields';
import { paymentSessionService } from '../../services/api';

function CardEntry(props) {
  const [gatewayConfig, setGatewayConfig] = useState(null);
  const [useLegacy, setUseLegacy] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    // Guard against double-fetch (React StrictMode)
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const detectGateway = async () => {
      try {
        const config = await paymentSessionService.getClientConfig();
        if (config?.features?.hostedFields) {
          setGatewayConfig(config);
        } else {
          setUseLegacy(true);
        }
      } catch (err) {
        // Gateway not configured or network error — fall back to legacy form
        console.warn('Gateway detection failed, using legacy card form:', err.message);
        setUseLegacy(true);
      } finally {
        // Detection complete (no state needed — rendering is driven by gatewayConfig/useLegacy)
      }
    };

    detectGateway();
  }, []);

  // Explicit fallback to legacy form
  if (useLegacy) {
    return <PaymentCardForm {...props} />;
  }

  // Render HPF form immediately (no spinner).
  // While detecting, gatewayConfig is null — HPF will fetch its own config.
  // Once detected, gatewayConfig is passed to skip the redundant API call.
  return (
    <HostedPaymentFields
      {...props}
      clientConfig={gatewayConfig}
    />
  );
}

export default CardEntry;
